//import "./highcharts.config";
import "./leaflet.config";
import "./parse.config";

import { init, produce, StorageAdapterLS } from "@opendash/core";
import { getCurrentLanguageSync } from "@opendash/i18n";
import { registerIconPack } from "@opendash/icons";
import { BDEPlugin } from "@opendash/plugin-bde";
import { DocumentationPlugin } from "@opendash/plugin-documentation";
import { GeoPlugin } from "@opendash/plugin-geo";
import { GeoPluginMapLibre } from "@opendash/plugin-geo-maplibre";
import { GTFSPlugin } from "@opendash/plugin-gtfs";
import { HighchartsPlugin } from "@opendash/plugin-highcharts";
import { KnowledgePlugin } from "@opendash/plugin-knowledge";
import { MobilityPlugin } from "@opendash/plugin-mobility";
import { MonitoringPlugin } from "@opendash/plugin-monitoring";
import { OpenServicePlugin } from "@opendash/plugin-openservice";
import { OpenwarePlugin } from "@opendash/plugin-openware";
import { $parse, ParsePlugin } from "@opendash/plugin-parse";
import { ParseMonitoringPlugin } from "@opendash/plugin-parse-monitoring";
import { PlotlyPlugin } from "@opendash/plugin-plotly";
import { SpreadsheetPlugin } from "@opendash/plugin-spreadsheet";
import { TimeseriesPlugin } from "@opendash/plugin-timeseries";
import dayjs from "dayjs";
import OverViewComponent from "./components/OverViewComponent";
import clientselector from "./config";

import timezone from "dayjs/plugin/timezone";

init("opendash", async (factory) => {
  // Logo
  if (clientselector.getLogoImage()) {
    factory.ui.setLogoImage(clientselector.getLogoImage());
  }

  // factory.ui.setLogoLink("/");
  // factory.ui.setLogoLinkExternal(true);
  // factory.ui.disableFooter();
  // factory.ui.disableHeaderSourcePicker();

  // Icons
  // @ts-ignore
  registerIconPack(await import("@opendash/icons/dist/fa-regular.json"));
  // @ts-ignore
  registerIconPack(await import("@opendash/icons/dist/fa-solid.json"));
  registerIconPack(await import("@opendash/icons/dist/fa-brands.json"));

  // Translations:
  factory.registerLanguage("en", "English");
  factory.registerLanguage("zh_Hans", "Chinese", "en", false);
  factory.registerLanguage("de", "Deutsch", "en", true);
  factory.registerLanguage("nl", "Niederländisch", "en", false);

  //@ts-ignore
  const params = new URL(document.location).searchParams;
  const embedded = params.get("embedded");

  if (embedded) {
    factory.ui.disableHeader();
    factory.ui.disableFooter();
  }

  factory.registerAntDesignTranslation(
    "en",
    () => import("antd/lib/locale/en_US")
  );

  factory.registerAntDesignTranslation(
    "de",
    () => import("antd/lib/locale/de_DE")
  );

  factory.registerAntDesignTranslation(
    "zh_Hans",
    () => import("antd/lib/locale/zh_CN")
  );

  factory.registerAntDesignTranslation(
    "nl",
    () => import("antd/lib/locale/nl_NL")
  );

  // Translations

  factory.registerTranslationResolver(
    "de",
    "parse-custom",
    async () => await import("./translations/parse/de.json")
  );

  factory.registerTranslationResolver(
    "en",
    "parse-custom",
    async () => await import("./translations/parse/en.json")
  );

  factory.registerTranslationResolver(
    "zh_Hans",
    "parse-custom",
    async () => await import("./translations/parse/zh_Hans.json")
  );

  factory.registerTranslationResolver(
    "nl",
    "parse-custom",
    async () => await import("./translations/parse/nl.json")
  );

  factory.registerTranslationResolver(
    "de",
    "widgets",
    async () => await import("./translations/widgets/de.json")
  );

  factory.registerTranslationResolver(
    "en",
    "widgets",
    async () => await import("./translations/widgets/en.json")
  );

  factory.registerTranslationResolver(
    "zh_Hans",
    "widgets",
    async () => await import("./translations/widgets/zh_Hans.json")
  );

  factory.registerTranslationResolver(
    "nl",
    "widgets",
    async () => await import("./translations/widgets/nl.json")
  );

  // Adapter + Plugins

  factory.registerDeviceStorageAdapter(new StorageAdapterLS());
  await factory.use(
    new ParsePlugin({
      authLdapActive: false,
      useLiveQueries: false,
      createAccount: false,
      lostPassword: false,
      usePagination: 20,
      disableNavigationItems: false,
      disableNavigationWidgetPreset: true,
      disableNavigationNotificationSettings: false,
    })
  );
  await factory.use(new TimeseriesPlugin());

  await factory.use(
    new MonitoringPlugin({
      hideMonitoringOnFrontPage: true,
      autoCreateInitialDashboard: true,
      autoCreateWidgetOptions: {
        autoCreateWidgets: false,
        autoCreateWidgetsForSource: false,
        autoCreateWidgetsContent: "dimension",
        autoCreateWidgetsContentForSource: "source",
        autoCreateWidgetsType: "opendash-widget-hc-kpi",
        autoCreateWidgetsForSourceType: "opendash-widget-hc-kpi",
        limitAutoCreateWidgets: 100,
      },
      widgetSorting: {
        enabled: false,
        valueTypeNames: ["string-of-valuetypes-includes"],
        autoSortOnFirstRender: {
          enabled: false,
          sourceTagIncludes: ["string-of-source-includes"],
        },
      },
    })
  );

  await factory.use(new GeoPlugin());
  await factory.use(new GeoPluginMapLibre());
  await factory.use(new OpenServicePlugin());
  await factory.use(new GTFSPlugin());
  await factory.use(new KnowledgePlugin());
  await factory.use(new PlotlyPlugin());
  await factory.use(new DocumentationPlugin());
  await factory.use(new BDEPlugin());

  await factory.use(
    new ParseMonitoringPlugin({
      liveQueries: false,
    })
  );

  await factory.use(
    new OpenwarePlugin({
      secure: clientselector.getOpenWareSecure(),
      host: clientselector.getOpenWareHost(),
      filterValuesOlderThanMS: -1,
      disableFeature: {
        menu: {
          SensorsGroup: false,
          DataPoints: true,
          DataSources: false,
          DataMeta: false,
          DataHierachies: true,
        },
        slideshow: true,
        dataCollection: true,
        VKPI: true,
        forms: {
          dateBySensor: false,
        },
        reporting: true,
        reportingFeatures: {
          adhoc: false,
          schedule: false,
          img: false,
          export: false,
        },
      },
    })
  );

  await factory.use(new HighchartsPlugin());
  await factory.use(new SpreadsheetPlugin());
  addcss(`
  .ant-modal-close-x {margin-top:16}
  .ant-steps-item-icon span {line-height:32px!important}
  .ant-steps-item-icon svg {margin-top:7}
  .ant-drawer-body .data-item-boolean {margin-top:12} 
  .ant-drawer-body .data-item-percentage {margin-top:10}
  .leaflet-top {z-index:998!important} 
  body {margin:0 !important}
  `);

  //@ts-ignore
  factory.setPrimaryColor("#666767");
  await factory.use(new MobilityPlugin());

  // await factory.use(new GreisPlugin());

  // factory.ui.disableHeader();

  // Widgets

  //$monitoring.registerWidget(await import("./widgets/kpi-map"));

  // Routing:

  // factory.registerRoute({
  //   path: "/route/to/*",
  //   component: () => import("./path/to/component")
  // });

  factory.registerRoute({
    path: "/overview/*",
    props: {},
    component: async () => ({ default: OverViewComponent }),
  });

  const currentConf = $parse.ui.getClassConfig("OD3_Source");
  const isAdmin =
    $parse.user.roles().find((role) => role === "od-admin") !== undefined;
  if (currentConf && isAdmin) {
    const newConf = produce(currentConf, (draft: any) => {
      const newConf = JSON.parse(JSON.stringify(draft));
      newConf.displayFields.push("tenant");
      newConf.editFields.push("tenant");
      newConf.createFields.push("tenant");
      newConf.editFields.push("tag");
      return newConf;
    });
    $parse.ui.setClassConfig(newConf);
  }
}).then((app) => {
  console.log("init open.DASH");
  dayjs.locale(getCurrentLanguageSync());
  dayjs.extend(timezone);
  dayjs.tz.setDefault("Europe/Berlin");
});

function addcss(css) {
  const head = document.getElementsByTagName("head")[0];
  const s = document.createElement("style");
  s.setAttribute("type", "text/css");
  s.appendChild(document.createTextNode(css));
  head.appendChild(s);
}
